import { Check } from "lucide-react";

import { cn } from "@/utils/cn";

export interface WizardStep {
  label: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
}

/**
 * docs/ui/component-inventory.md's `Stepper`/`WizardStepper`: desktop gets
 * the full labeled-step + connector-line variant, mobile collapses to a
 * segmented progress bar + "Step X of N" text — one component, not two
 * implementations to keep in sync (docs/ui/design-system.md §11's own rule
 * for `FilterSidebar` applies just as much here).
 */
export function WizardStepper({ steps, currentStep }: WizardStepperProps) {
  return (
    <div>
      <div className="hidden items-center sm:flex">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          return (
            <div key={step.label} className="flex flex-1 items-center last:flex-none">
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-body-sm font-medium",
                    isCompleted && "bg-primary text-primary-foreground",
                    isCurrent && "border-2 border-primary text-primary",
                    !isCompleted && !isCurrent && "border border-border text-muted-foreground",
                  )}
                >
                  {isCompleted ? <Check className="h-4 w-4" aria-hidden="true" /> : stepNumber}
                </div>
                <span
                  className={cn(
                    "text-body-sm",
                    isCurrent ? "font-medium text-foreground" : "text-muted-foreground",
                  )}
                >
                  {step.label}
                </span>
              </div>
              {stepNumber < steps.length && (
                <div
                  className={cn(
                    "mx-3 h-px flex-1",
                    isCompleted ? "bg-primary" : "bg-border",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="sm:hidden">
        <p className="mb-2 text-body-sm text-muted-foreground">
          Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.label}
        </p>
        <div className="flex gap-1.5">
          {steps.map((step, index) => (
            <div
              key={step.label}
              className={cn(
                "h-1.5 flex-1 rounded-full",
                index + 1 <= currentStep ? "bg-primary" : "bg-border",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
